import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, ReplaySubject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { File } from '@ionic-native/file/ngx';
import { Platform } from '@ionic/angular';

import { ProfileService } from "../profile/profile.service"

@Injectable({
  providedIn: 'root'
})
export class ContentService {
  constructor(private http: HttpClient, private file: File, private platform: Platform,
    private zone: NgZone, private profileService: ProfileService) {

  }

  private staticContent = new BehaviorSubject<any>({ lastchange: 0 });
  private cacheFileList = [];
  private cacheFileListLoaded = false;

  public getStaticContent(): Observable<any> {
    if (!this.staticContent.getValue().lastchange) {
      //load from cache
      const cache = JSON.parse(localStorage.getItem("staticContentCache"));
      if (cache)
        this.staticContent.next(cache);
      this.loadStaticContent();
    }
    return this.staticContent
  }


  public loadStaticContent() {
    let URL = environment.contentURL + '/getStaticContent';
    let body = {
      "apikey": environment.contentAPIKey,
      "bookcode": environment.bookcode
    };
    let req = this.http.post(URL, body);
    req.subscribe((data) => {
      let old = this.staticContent.getValue();
      this.staticContent.next(data);
      localStorage.setItem("staticContentCache", JSON.stringify(data));     //save to cache
      this.handleCacheUpdates(old, data);
    });
  }


  private async handleCacheUpdates(oldD, newD) {
    await this.isFileCached('---'); //make sure the cache list exists already or wait for it

    //establish a full list of files for the current language code
    let lc = this.profileService.languageCodeNow();
    let npageList = Object.keys(newD.pages);
    let reqFiles = npageList.reduce((acc, page) => acc.concat(this.getReqPageFiles(page, lc, newD.pages[page])), [])

    console.log("possible pages that could be cached",reqFiles);

    let cacheTasks = [];

    //establish any pages (files) we might have that have been updated
    if (oldD&&oldD.pages) {
      let opageList = Object.keys(oldD.pages);
      let oldFiles = opageList.reduce((acc, page) => acc.concat(this.getOldPageFiles(page, oldD.pages, newD.pages)), [])


      //see if we have any currently cached files that are not in the reqFiles list:
      let unNeeded = this.cacheFileList.filter((fn) => !reqFiles.includes(fn))
      //add them to the old files list as they should also go
      oldFiles = oldFiles.concat(unNeeded);

      console.log("pages that need dumping:",oldFiles);
      //create tasks to remove files
      oldFiles.forEach(fn => cacheTasks.push(() => this.removeCacheFile(fn)));
      //then a task to clear the blob cache
      cacheTasks.push(async () => { this.blobCache = {}; console.log('soft cache reset'); });
    }

    //if the profile requires it
    if (this.profileService.cacheCodeNow()=='ALL') //create load tasks for all the
      reqFiles.forEach(fn => cacheTasks.push(() => this.preloadCacheFile(fn)));


    function runCacheTask() {
      let task = cacheTasks.shift();
      if (!task) {
        console.log("tasks complete")
        return; //we are done!
      }
      task().then(() => { runCacheTask(); },
        () => {  runCacheTask(); })
    }
    //start the tasks queue
    runCacheTask();

  }

  private getOldPageFiles(page, oldD, newD) {
    let oldP = oldD[page];
    let newP = newD[page] || {};
    if (!oldP.content) return [];
    let langList = Object.keys(oldP.content);
    return langList.reduce((acc, l) =>
      acc.concat(this.getOldPageFilesLang(page, l.substring(8), oldP.content[l], newP.content[l])), [])
  }

  private getOldPageFilesLang(page, l, oldL, newL): string[] {
    if (oldL.lastChange == newL.lastChange) return [];
    return this.getFileLangList(page, l, oldL);
  }

  private getReqPageFiles(pageRef, lc, page) {
    if (!page.content) return [];
    let con = page.content["__LANG__" + lc] || page.content["__LANG__ENG"];
    return this.getFileLangList(pageRef, lc, con);
  }

  private getFileLangList(page, lang, con): string[] {
    if (!con) return []; //no content in this language
    let files = [page + "_" + lang + ".md"];
    if (con.images)
      con.images.split(' ').forEach((ext, i) => {
        if (ext && (!ext.startsWith("-"))&&(!ext.startsWith("mp4"))) files.push(page + "_" + lang + "_" + (i + 1) + "." + ext)
      })
    return files;
  }


  private blobCache = {};


  public clearCaches() {
    this.blobCache = {};
    this.loadStaticContent();
  }

  public getStaticResource(pageRef: string, langCode: string, imageIndex = -1) {
    let ext = '.md';
    //modify for  images
    if (imageIndex > 0)
      ext = this.getImageExt(pageRef, langCode, imageIndex)

    let fn = pageRef + "_" + langCode + ext;
    //if we have a subject in the cache that matches then we can just return that
    if (this.blobCache[fn]) return this.blobCache[fn];
    //otherwise generate a new subject, subscribe to the call and add it to the cache
    let sub = new ReplaySubject(1); //only replay once
    this.blobCache[fn] = sub;
    this.getFile(fn, sub)
    return sub;
  }

  public getMediaStreamURL(pageRef: string, langCode: string, imageIndex:number) {
    //modify for  images
    let ext = this.getImageExt(pageRef, langCode, imageIndex);
    let fn = pageRef + "_" + langCode + ext;
    return environment.storageURL+environment.bookcode.toLowerCase()+'%2f'+fn+'?alt=media';
  }


  public decacheStaticResource(pageRef: string, langCode: string, imageIndex = -1) {
    let ext = '.md';
    //modify for  images
    if (imageIndex > 0)
      ext = this.getImageExt(pageRef, langCode, imageIndex)

    let fn = pageRef + "_" + langCode + ext;
    //if we have a subject in the cache remove it
    if (this.blobCache[fn]) delete this.blobCache[fn];

    //do the same on the url cache - no longer exists at this level
    //let k = pageRef+langCode+imageIndex;
    //if (this.URLCache[k]) delete this.URLCache[k];
  }

  private getFile(fn, sub) {
    this.isFileCached(fn).then((inCache) => {
      if (inCache)
        this.loadBlobFromCache(fn).subscribe(sub);
      else
        this.loadBlobFromCloud(fn).subscribe(sub);
    })
  }

  private loadBlobFromCloud(fn) {
    let URL = environment.contentURL + '/getStaticResource';
    let body = {
      "apikey": environment.contentAPIKey,
      "bookcode": environment.bookcode,
      "filename": fn,
    };
    console.log("requesting file from cloud: " + fn);
    return this.http.post(URL, body, { responseType: 'blob', observe: 'response' })
      .pipe(tap(res => this.saveBlobToCache(fn, res.body))) //save it cache if we can
  }

  private async saveBlobToCache(fn, blob) {
    if (this.platform.is('cordova')) {//if we are on device
      //check out the cache for this file
      this.file.writeFile(this.file.dataDirectory,
        fn,
        blob,
        {
          replace: true,
        }
      ).then(res => {
        console.log("File Saved:" + this.file.dataDirectory + fn)
        this.cacheFileList.push(fn);
      });
    }
  }

  private loadBlobFromCache(fn) {
    console.log("fetching from cache: " + fn);
    return new Observable(subscriber => {
      this.file.resolveDirectoryUrl(this.file.dataDirectory)
        .then(dir => this.file.getFile(dir, fn, { create: false }))
        .then((fileEntry: any) => {
          fileEntry.file(file => {
            var reader = new FileReader();
            reader.onloadend = (e) => {
              let val: any = reader.result;
              if (val) {//happy case we loaded it
                //figure out mime type
                let ctype = 'text/markdown';
                let ext = fn.split('.').pop();
                if (ext.toLowerCase() == 'jpg') ctype = 'image/jpeg';
                if (ext.toLowerCase() == 'jpeg') ctype = 'image/jpeg';
                if (ext.toLowerCase() == 'png') ctype = 'image/png';

                var blob = new Blob([new Uint8Array(val)], { type: ctype });
                this.zone.run(() => {
                  subscriber.next({ body: blob });
                  subscriber.complete();
                });
              } else { //didn't actually get it
                this.loadBlobFromCloud(fn).subscribe(res=>{
                  this.zone.run(() => {
                    subscriber.next(res);
                    subscriber.complete();
                  });
                });
              }
            };
            reader.readAsArrayBuffer(file);
          })
        }).
        catch(err => {
          console.log("Failed on cache load: " + fn, err);
          this.loadBlobFromCloud(fn).subscribe(res=>{
            this.zone.run(() => {
              subscriber.next(res);
              subscriber.complete();
            });
          });
        });
    });
  }

  private async removeCacheFile(fn) {
    if (!this.platform.is('cordova')) return;
    return this.isFileCached(fn).then((present) => {
      if (!present) return;
      this.file.resolveDirectoryUrl(this.file.dataDirectory)
        .then((dir) => this.file.getFile(dir, fn, { create: false }))
        .then((fileEntry) => {
          this.cacheFileList = this.cacheFileList.filter(f => (f != fn));
          return new Promise((resolve, reject) => {
            fileEntry.remove(() => {
              console.log("deleted  "+fn);
              resolve(true);
            }, (err) => {
              console.log("deleted Fail ", err);
              reject(err);
            });
          });
        });
    });
  }

  private async preloadCacheFile(fn)
  {
    if (!this.platform.is('cordova')) return;
    if (await this.isFileCached(fn)) return;
    console.log("preload "+fn)
    return this.loadBlobFromCloud(fn).toPromise();

  }

  private isFileCached(fn): Promise<boolean> {
    if (!this.platform.is('cordova')) return Promise.resolve(false);
    if (this.cacheFileListLoaded)
      return Promise.resolve(this.cacheFileList.includes(fn))
    return this.platform.ready()
      .then((sr) => this.file.listDir(this.file.dataDirectory, ''))
      .then((listing) => {
        if (!this.cacheFileListLoaded) {
          this.cacheFileList = listing.filter(e => e.isFile).map(e => e.name);
          console.log("got cache listing", this.cacheFileList);
        }
        this.cacheFileListLoaded = true;
        return this.cacheFileList.includes(fn);
      }).
      catch(e => {
        console.log(" catch in isFileCached ", e);
        return false;
      });
  }

  public getMediaType(pageRef: string, langcode: string, imageIndex: number)
  {
    let lc = this.staticContent.getValue().pages[pageRef].content["__LANG__" + langcode];
    if ((!lc) || (!lc.images)) return ""
    let ia = lc.images.split(" ");
    if (imageIndex > ia.length) return "";
    let ext = ia[imageIndex - 1];
    if (ext == '-') return "";
    return ext.toLowerCase();
  }



  private getImageExt(pageRef: string, langcode: string, imageIndex: number): string {
    let lc = this.staticContent.getValue().pages[pageRef].content["__LANG__" + langcode];
    if ((!lc) || (!lc.images)) return ""
    let ia = lc.images.split(" ");
    if (imageIndex > ia.length) return "";
    let ext = ia[imageIndex - 1];
    if (ext == '-') return "";
    return ("_" + imageIndex + "." + ext).toLowerCase();
  }




}
